/* @flow */

import React from 'react';
import { useIntl } from 'react-intl';
import { useMediaQuery } from '@mui/material';

// Styles
import createUseThemeStyles from 'src/shared/app/base/util/createUseThemeStyles';
import styles from './SideBar.style';

// Utils
import messages from 'src/shared/app/nav/l10n/navigationL10n.json';
import { newTopicRoute } from 'src/shared/app/base/route/setting/routeSettings';
import { tablet } from '@braindate/ui/lib/token/mediaQuery';

// Hooks
import useRoutes from 'src/shared/app/base/route/hook/useRoutes';

// Components
import Fixed from 'src/shared/app/base/component/fixed/Fixed';
import LinkButton from 'src/shared/ui/component/link/LinkButton';
import SurveyCard from './survey/SurveyCard';
import SponsorsCards from './sponsors/SponsorsCards';
import PlusOutlineIcon from 'src/shared/ui/component/icon/PlusOutlineIcon';

import type { BraindateEventSponsor } from '@braindate/domain/lib/event/type';
import classNames from 'classnames';
import { TOUR_SELECTOR_KEYS } from 'src/shared/app/coachmark/config/tourConfig';

const useStyles = createUseThemeStyles(styles);

type Props = {
  showSurvey?: boolean,
  small?: boolean,
  showCreateTopicButton?: boolean,
  eventSponsors: BraindateEventSponsor[],
};

const Sidebar = ({
  small,
  showSurvey,
  showCreateTopicButton,
  eventSponsors,
}: Props) => {
  const classes = useStyles({ small });
  const intl = useIntl();
  const shouldBeShown = useMediaQuery(tablet);

  const [newTopicPath] = useRoutes(newTopicRoute);

  if (!shouldBeShown) return null;

  return (
    <div>
      <Fixed>
        <aside className={classes.inner}>
          {showCreateTopicButton && (
            <LinkButton
              to={newTopicPath}
              layoutClass={classNames(
                classes.createNewTopic,
                TOUR_SELECTOR_KEYS.CREATE_TOPIC_BUTTON,
              )}
            >
              <PlusOutlineIcon className={classes.plusIcon} />
              {intl.formatMessage(messages.createNewTopic)}
            </LinkButton>
          )}

          <SponsorsCards eventSponsors={eventSponsors} />

          <SurveyCard showSurvey={showSurvey} />
        </aside>
      </Fixed>
    </div>
  );
};

export default React.memo<Props>(Sidebar);
